import { translationKey, readAppLanguage } from '../i18n';
import { localize } from '../utils/interfaceCopy';
import useNavigate from '../hooks/useSafeNavigate';
import DialogTitle from '../components/ui/FitDialogTitle';
import SingleLineText from '../components/ui/InternationalText';
import Button from '../components/ui/FitButton';
import AppButton from '../components/ui/AppButton';
import { journey } from '../utils/productJourneys';
import Dialog from '../components/StableDialog';
import FamilyAccountNotice from '../components/FamilyAccountNotice';
import PageLoading from '../components/PageLoading';
import ConnectionRetryState from '../components/ConnectionRetryState';
import useRequestGuard from '../hooks/useRequestGuard';
import LoadingLabel from '../components/LoadingLabel';
import React, { useState, useEffect, useCallback, useRef } from 'react';

import { Box, Typography, CircularProgress, TextField, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { ToggleButton, ToggleButtonGroup, IconButton } from '@mui/material';
import PageHeader from '../components/PageHeader';
import NotificationPermissionBanner from '../components/NotificationPermissionBanner';
import ScrollDownIndicator from '../components/ScrollDownIndicator';
import AppToast from '../components/AppToast';
import BottomSheet from '../components/BottomSheet';
import GuardianConfirmSheet from '../components/GuardianConfirmSheet';
import GuardianNudgeSheet from '../components/GuardianNudgeSheet';
import {
    getDeviceUuid, getGuardianTargets, getGuardianTargetToday, getGuardianLinkSettings,
    updateGuardianLinkSettings, confirmGuardianLogs, sendGuardianNudge, claimGuardianNudgeRewardBonus,
    getMyGuardians, removeGuardianLink, updateGuardianLinkNickname, updateGuardianTargetNickname,
} from '../api';
import { requestAd, rewardedAdFailureMessage } from '../utils/adRequests';
import { clearHomeCache } from '../utils/localCache';
import { guardianOverdueMinutes, formatGuardianLogTime, sortGuardianLogs, canNudgeGuardianLog, guardianSharingNotice } from '../utils/guardianLogTiming';
import { isPauseActive } from '../utils/medicationPause';

const familyCardSx = {
    mb: 2,
    borderRadius: 4,
    bgcolor: '#fff',
    overflow: 'hidden',
    border: '1px solid #f0f0f0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
};

const familyCardHeaderSx = {
    px: 2,
    pt: 1.5,
    pb: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
};

const familyAvatarSx = {
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
};

const cardIconButtonSx = {
    color: '#111',
    bgcolor: 'transparent',
    border: '1px solid #e5e7eb',
    '&:hover': { bgcolor: 'transparent' },
    '&:active': { bgcolor: 'transparent' },
};

const familyNameActionSx = {
    ...cardIconButtonSx,
    alignSelf: 'flex-start',
    width: 38,
    height: 38,
    mt: 'calc(1.15rem * 1.5 / 2 - 19px)',
};

function FamilySectionHeader({ title, subtitle, actionLabel, onAction, sx }) {
    return (
        <Box sx={{ mt: 3, mb: 1.2, px: 0.5, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1.5, ...sx }}>
            <Box sx={{ minWidth: 0 }}>
                <SingleLineText sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#1a1a1a', lineHeight: 1.25 }}>
                    {title}
                </SingleLineText>
                {subtitle && (
                    <SingleLineText sx={{ mt: 0.3, fontSize: '0.9rem', color: '#8a8a8a', fontWeight: 500, lineHeight: 1.35 }}>
                        {subtitle}
                    </SingleLineText>
                )}
            </Box>
            <Button
                size="small"
                startIcon={<PersonAddIcon sx={{ fontSize: 16 }} />}
                onClick={onAction}
                sx={{
                    flexShrink: 0,
                    minWidth: 0,
                    px: 1.2,
                    py: 0.6,
                    borderRadius: 2.5,
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                    color: '#5a4a00',
                    fontWeight: 800,
                    fontSize: '0.86rem',
                    '&:hover': { bgcolor: '#fffbeb', borderColor: '#f4cf63' },
                    '&:active': { bgcolor: '#fff7d6' },
                }}
            >
                {actionLabel}
            </Button>
        </Box>
    );
}

const formatMedicationSummary = (names = []) => {
    if (names.length === 0) return '';
    const visible = names.slice(0, 2).join(', ');
    const hiddenCount = names.length - 2;
    return hiddenCount > 0 ? localize("{v0} 외 {v1}개", { v0: visible, v1: hiddenCount }) : visible;
};

export default function GuardianPage() {
    const international = readAppLanguage() !== 'ko';
    const journeyLoaded = useRef(false);
    const recordJourneyLoaded = () => { if (!journeyLoaded.current) { journeyLoaded.current = true; journey('family_status', 'complete'); } };
    const navigate = useNavigate();
    const [targets, setTargets] = useState([]);
    const [targetData, setTargetData] = useState({});
    const [targetMedNames, setTargetMedNames] = useState({}); // groupId -> [medName, ...]
    const targetMedNamesRef = React.useRef({});
    const [myGuardians, setMyGuardians] = useState([]);
    const [editingNicknameId, setEditingNicknameId] = useState(null);
    const [editingValue, setEditingValue] = useState('');
    const [savingNickname, setSavingNickname] = useState(false);
    const [confirmRemove, setConfirmRemove] = useState(null);
    const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [settingsSheet, setSettingsSheet] = useState(null); // { linkId, ... }
    const [confirmSheet, setConfirmSheet] = useState(null); // { target, logs }
    const [nudgeSheet, setNudgeSheet] = useState(null); // { target, logs }
    const [settingsForm, setSettingsForm] = useState({ notifyOnMissed: true, notifyDelayMinutes: 0, notifyOnTaken: true, patientNickname: '', excludedMedicationIds: [] });
    const [settingsNicknameDraft, setSettingsNicknameDraft] = useState('');
    const [editingTargetNickname, setEditingTargetNickname] = useState(false);
    const [nicknameError, setNicknameError] = useState('');
    const [targetMeds, setTargetMeds] = useState([]);
    const [savingSettings, setSavingSettings] = useState(false);
    const [savingSettingsNickname, setSavingSettingsNickname] = useState(false);
    const [customDelayInput, setCustomDelayInput] = useState(false);
    const [customDelayValue, setCustomDelayValue] = useState('');
    const [nudgeSubmitting, setNudgeSubmitting] = useState(false);
    const [rewardSubmitting, setRewardSubmitting] = useState(false);
    const [nudgeLimitReached, setNudgeLimitReached] = useState(false);
    const [nudgeRewardAvailable, setNudgeRewardAvailable] = useState(true);
    const [nudgeLimitDialogOpen, setNudgeLimitDialogOpen] = useState(false);
    const beginRequest = useRequestGuard('guardian');
    const mutatingRef = useRef(0);
    const fetchInFlightRef = useRef(null);
    const lastFetchCompletedAtRef = useRef(0);

    const fetchData = useCallback(async (force = false) => {
        if (mutatingRef.current > 0) return;
        if (!force && fetchInFlightRef.current) return fetchInFlightRef.current;
        if (!force && Date.now() - lastFetchCompletedAtRef.current < 5000) return;

        const isCurrent = beginRequest();
        const run = (async () => {
            try {
                const uuid = getDeviceUuid();
                const [raw, guardians] = await Promise.all([
                    getGuardianTargets(uuid),
                    getMyGuardians(uuid),
                ]);
                if (!isCurrent()) return;
                // 백엔드 푸시 닉네임 fallback과 인덱스 일치시키기 위해 linkId 오름차순 정렬
                const t = [...raw].sort((a, b) => (a.linkId ?? 0) - (b.linkId ?? 0));
                setTargets(t);
                setMyGuardians(guardians);
                clearHomeCache();
                setError(false);

                // Fetch today data for each target
                const dataMap = {};
                const results = await Promise.allSettled(
                    t.map(async (target) => {
                        const today = await getGuardianTargetToday(uuid, target.groupId);
                        return { groupId: target.groupId, today };
                    })
                );
                if (!isCurrent()) return;
                let failCount = 0;
                for (const [index, r] of results.entries()) {
                    if (r.status === 'fulfilled') {
                        dataMap[r.value.groupId] = {
                            ...r.value.today,
                            logs: sortGuardianLogs(r.value.today.logs || []),
                        };
                    } else {
                        failCount++;
                        dataMap[t[index].groupId] = { loadError: true, logs: [] };
                    }
                }
                setTargetData(dataMap);
                if (t.length > 0 && failCount < t.length) recordJourneyLoaded();

                // 오늘 로그가 없는 타겟의 약 목록 가져오기 (ref로 최신 캐시 참조)
                const medNameMap = { ...targetMedNamesRef.current };
                const noLogTargets = t.filter(target => {
                    const d = dataMap[target.groupId];
                    return target.deviceCount > 0 && d && !d.loadError && (!d.logs || d.logs.length === 0);
                });
                if (noLogTargets.length > 0) {
                    const medResults = await Promise.allSettled(
                        noLogTargets.map(async (target) => {
                            const settings = await getGuardianLinkSettings(uuid, target.linkId);
                            // AS_NEEDED 약은 schedule(times) 없음 — 가족 알림 대상 아니므로 메인 카드에서도 제외.
                            // 백엔드 필터링과 별개 안전망.
                            const meds = (settings.medications || []).filter(
                                m => Array.isArray(m.times) && m.times.length > 0
                            );
                            return { groupId: target.groupId, meds };
                        })
                    );
                    if (!isCurrent()) return;
                    for (const r of medResults) {
                        if (r.status === 'fulfilled') {
                            medNameMap[r.value.groupId] = r.value.meds.map(m => m.name);
                        }
                    }
                    targetMedNamesRef.current = medNameMap;
                    setTargetMedNames(medNameMap);
                }

                if (failCount > 0) {
                    setSnackbar({ open: true, message: localize("일부 데이터를 불러오지 못했어요 ({v0}건)", { v0: failCount }), severity: 'warning' });
                }
            } catch (err) {
                if (!isCurrent()) return;
                console.error('Failed to load guardian targets:', err);
                setError(true);
                setSnackbar({ open: true, message: localize("데이터를 불러오지 못했어요"), severity: 'error' });
            } finally {
                if (isCurrent()) {
                    lastFetchCompletedAtRef.current = Date.now();
                    setLoading(false);
                }
            }
        })();

        fetchInFlightRef.current = run;
        try {
            await run;
        } finally {
            if (fetchInFlightRef.current === run) fetchInFlightRef.current = null;
        }
    }, [beginRequest]);

    useEffect(() => {
        fetchData(true);
        const interval = setInterval(fetchData, 15000);
        const handleVisibility = () => { if (document.visibilityState === 'visible') fetchData(true); };
        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('focus', handleVisibility);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('focus', handleVisibility);
        };
    }, [fetchData]);

    const handleRewardComplete = useCallback(async () => {
        setRewardSubmitting(true);
        try {
            const uuid = getDeviceUuid();
            const res = await claimGuardianNudgeRewardBonus(uuid);
            if (res.success) {
                setNudgeLimitReached(false);
                setNudgeLimitDialogOpen(false);
                setSnackbar({ open: true, message: localize("챙김 요청 알림 {v0}회 사용 가능해졌어요", { v0: res.remaining }), severity: 'success' });
            } else {
                if (res.bonusRemaining === 0) setNudgeRewardAvailable(false);
                setSnackbar({ open: true, message: res.message || localize('보너스를 받을 수 없어요'), severity: 'warning' });
            }
        } catch {
            setSnackbar({ open: true, message: localize("보너스 처리 중 오류가 발생했어요"), severity: 'error' });
        } finally {
            setRewardSubmitting(false);
        }
    }, []);

    useEffect(() => {
        const handler = (event) => {
            try {
                const data = JSON.parse(event.data);
                const feature = data.feature || 'guardian_nudge_reward';
                if (feature !== 'guardian_nudge_reward') return;
                if (data.type === 'REWARDED_AD_COMPLETE') handleRewardComplete();
                if (data.type === 'REWARDED_AD_UNAVAILABLE') {
                    setSnackbar({ open: true, message: rewardedAdFailureMessage(data.reason), severity: 'warning' });
                }
            } catch {}
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [handleRewardComplete]);

    const [settingsLoadingId, setSettingsLoadingId] = useState(null);
    const [removingGuardian, setRemovingGuardian] = useState(false);
    const openSettings = async (linkId) => {
        if (settingsLoadingId !== null) return;
        beginRequest('settingsNickname');
        setSettingsLoadingId(linkId);
        try {
            const uuid = getDeviceUuid();
            const settings = await getGuardianLinkSettings(uuid, linkId);
            setSettingsForm({
                notifyOnMissed: settings.notifyOnMissed,
                notifyDelayMinutes: settings.notifyDelayMinutes,
                notifyOnTaken: settings.notifyOnTaken,
                patientNickname: settings.patientNickname || '',
                excludedMedicationIds: settings.excludedMedicationIds || [],
            });
            setSettingsNicknameDraft(settings.patientNickname || '');
            setTargetMeds(settings.medications || []);
            setEditingTargetNickname(false);
            setNicknameError('');
            setSettingsSheet({ linkId });
        } catch {
            setSnackbar({ open: true, message: localize("설정을 불러오지 못했어요"), severity: 'error' });
        } finally { setSettingsLoadingId(null); }
    };

    const normalizeImmediateSettings = (form) => {
        const next = { ...form };
        if (!next.notifyOnMissed) {
            next.notifyDelayMinutes = 0;
            return next;
        }
        if (next.notifyDelayMinutes <= 0) return next;

        const includedReminderMeds = targetMeds.filter(med =>
            !next.excludedMedicationIds.includes(med.id) && med.reminderEnabled
        );
        const minMissedMinutes = includedReminderMeds.reduce(
            (min, med) => Math.min(min, med.maxReminderMinutes),
            Infinity
        );
        if (minMissedMinutes === Infinity || minMissedMinutes <= 1) {
            next.notifyDelayMinutes = 0;
            return next;
        }
        next.notifyDelayMinutes = Math.min(next.notifyDelayMinutes, minMissedMinutes - 1);
        return next;
    };

    const saveSettingsChange = async (nextForm) => {
        if (!settingsSheet || savingSettings || savingSettingsNickname) return;
        const previousForm = settingsForm;
        const normalizedForm = normalizeImmediateSettings(nextForm);
        setSettingsForm(normalizedForm);
        setSavingSettings(true);
        try {
            const uuid = getDeviceUuid();
            await updateGuardianLinkSettings(uuid, settingsSheet.linkId, normalizedForm);
            clearHomeCache();
            beginRequest();
            fetchData(true);
        } catch {
            setSettingsForm(previousForm);
            setSnackbar({ open: true, message: localize("설정 저장 실패"), severity: 'error' });
        } finally {
            setSavingSettings(false);
        }
    };

    const handleSaveTargetNickname = async () => {
        if (!settingsSheet || savingSettings || savingSettingsNickname) return;
        const isCurrent = beginRequest('settingsNickname');
        const nickname = settingsNicknameDraft.trim();
        if (nickname.length > 30 || nickname === settingsForm.patientNickname.trim()) return;
        const nextForm = { ...settingsForm, patientNickname: nickname };
        setNicknameError('');
        setSavingSettingsNickname(true);
        try {
            const uuid = getDeviceUuid();
            await updateGuardianTargetNickname(uuid, settingsSheet.linkId, nickname);
            clearHomeCache();
            if (!isCurrent()) return;
            beginRequest();
            setSettingsForm(nextForm);
            setSettingsNicknameDraft(nickname);
            setTargets(prev => prev.map(t =>
                t.linkId === settingsSheet.linkId ? { ...t, patientNickname: nickname } : t
            ));
            setEditingTargetNickname(false);
            setSnackbar({ open: true, message: localize("이름을 변경했어요"), severity: 'success' });
        } catch {
            if (!isCurrent()) return;
            setNicknameError(translationKey('이름을 저장하지 못했어요. 다시 시도해 주세요.'));
        } finally {
            setSavingSettingsNickname(false);
        }
    };

    const targetDefaultName = localize("가족 {v0}", { v0: Math.max(0, targets.findIndex(target => target.linkId === settingsSheet?.linkId)) + 1 });
    const targetDisplayName = settingsForm.patientNickname || targetDefaultName;
    const nicknameTooLong = settingsNicknameDraft.trim().length > 30;
    const nicknameUnchanged = settingsNicknameDraft.trim() === settingsForm.patientNickname.trim();
    const cancelTargetNickname = () => {
        beginRequest('settingsNickname');
        setSettingsNicknameDraft(settingsForm.patientNickname);
        setNicknameError('');
        setEditingTargetNickname(false);
    };

    // 보호자가 가족 약을 N개 한 번에 완료 처리.
    // 서버 bulk API 한 번으로 처리해서 같은 묶음이 일부만 반영되는 상황을 막는다.
    const handleGuardianConfirm = async (groupId, logIds, confirmedAt) => {
        const uuid = getDeviceUuid();
        const idSet = new Set(logIds);
        mutatingRef.current++;
        beginRequest(); // Invalidate reads that started before the optimistic update.

        // 옵티미스틱: 선택된 로그를 즉시 TAKEN으로 표시
        const before = targetData[groupId];
        setTargetData(prev => {
            const today = prev[groupId];
            if (!today) return prev;
            return {
                ...prev,
                [groupId]: {
                    ...today,
                    logs: sortGuardianLogs(today.logs.map(l => idSet.has(l.id) ? { ...l, status: 'TAKEN', confirmedAt } : l)),
                },
            };
        });

        try {
            await confirmGuardianLogs(uuid, groupId, logIds, confirmedAt);
            clearHomeCache();
            setSnackbar({ open: true, message: localize("{v0}개 완료 처리했어요", { v0: logIds.length }), severity: 'success' });
        } catch (err) {
            setTargetData(prev => {
                const today = prev[groupId];
                if (!today || !before) return prev;
                const beforeMap = new Map(before.logs.map(l => [l.id, l]));
                return {
                    ...prev,
                    [groupId]: {
                        ...before,
                        logs: sortGuardianLogs(today.logs.map(l => idSet.has(l.id) ? (beforeMap.get(l.id) || l) : l)),
                    },
                };
            });
            setSnackbar({
                open: true,
                message: err?.response?.data?.message || err?.response?.data?.error || localize("완료 처리에 실패했어요. 다시 시도해주세요"),
                severity: 'error',
            });
            throw err;
        } finally {
            // Forced reconciliation must not be skipped by the polling throttle.
            mutatingRef.current--;
            fetchData(true);
        }
    };

    const handleGuardianNudge = async (payload) => {
        if (!nudgeSheet) return;
        const representativeLog = nudgeSheet.logs?.[0];
        if (!representativeLog) {
            setSnackbar({ open: true, message: localize("보낼 수 있는 약 정보가 없어요"), severity: 'warning' });
            return;
        }
        setNudgeSubmitting(true);
        try {
            const uuid = getDeviceUuid();
            await sendGuardianNudge(uuid, nudgeSheet.target.groupId, representativeLog.id, payload);
            setNudgeLimitReached(false);
            setNudgeSheet(null);
            setSnackbar({
                open: true,
                message: localize("챙김 요청 알림을 보냈어요"),
                severity: 'success',
            });
        } catch (err) {
            if (err.response?.status === 429) {
                setNudgeRewardAvailable((err.response.data?.bonusRemaining ?? 0) > 0);
                setNudgeLimitReached(true);
                setNudgeLimitDialogOpen(true);
            } else if (err.response?.status === 409) {
                setSnackbar({ open: true, message: err.response?.data?.message || localize("전송 가능한 대상 기기가 없어요"), severity: 'warning' });
            } else {
                setSnackbar({ open: true, message: err.response?.data?.message || localize("챙김 요청 알림 전송에 실패했어요"), severity: 'error' });
            }
        } finally {
            setNudgeSubmitting(false);
        }
    };

    const requestRewardedAd = () => {
        if (!requestAd('rewarded', 'guardian_nudge_reward')) {
            setSnackbar({ open: true, message: localize("광고를 열 수 없는 환경이에요"), severity: 'warning' });
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'TAKEN': return '#22c55e';
            case 'MISSED': return '#ef4444';
            case 'SKIPPED': return '#9ca3af';
            default: return '#facc15';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'TAKEN': return localize("완료");
            case 'MISSED': return localize("미복용");
            case 'SKIPPED': return localize("건너뜀");
            default: return localize("대기 중");
        }
    };

    const handleSaveGuardianNickname = async (guardian) => {
        setSavingNickname(true);
        try {
            const uuid = getDeviceUuid();
            const nickname = editingValue.trim() || null;
            await updateGuardianLinkNickname(uuid, guardian.linkId, nickname);
            clearHomeCache();
            beginRequest();
            setMyGuardians(prev => prev.map(x =>
                x.linkId === guardian.linkId ? { ...x, guardianNickname: nickname } : x
            ));
            setSnackbar({ open: true, message: localize("별칭이 저장되었어요"), severity: 'success' });
        } catch {
            setSnackbar({ open: true, message: localize("저장 실패"), severity: 'error' });
        } finally {
            setSavingNickname(false);
            setEditingNicknameId(null);
        }
    };

    const handleRemoveMyGuardian = async () => {
        const guardian = confirmRemove;
        if (!guardian || removingGuardian) return;
        setRemovingGuardian(true);
        try {
            const uuid = getDeviceUuid();
            await removeGuardianLink(uuid, guardian.linkId);
            beginRequest();
            setConfirmRemoveOpen(false);
            setMyGuardians(prev => prev.filter(x => x.linkId !== guardian.linkId));
            clearHomeCache();
            setSnackbar({ open: true, message: localize("보호자 연결 해제됨"), severity: 'success' });
        } catch {
            setSnackbar({ open: true, message: localize("해제 실패"), severity: 'error' });
        } finally { setRemovingGuardian(false); }
    };

    if (loading) {
        return (
            <PageLoading />
        );
    }

    return (
        <Box sx={{ background: '#f5f5f5', pb: 2 }}>
            <PageHeader back title={localize("가족 돌봄 현황")} />

            <Box sx={{ px: 2, mt: 3, '& > *:last-child': { mb: 0 } }}>
                <NotificationPermissionBanner needed={targets.length > 0 || myGuardians.length > 0} />
                {!error && targets.length === 0 && myGuardians.length === 0 && <FamilyAccountNotice />}
                <FamilySectionHeader
                    title={localize("내가 챙기는 가족")}
                    subtitle={targets.length > 0 ? (targets.every(t => t.deviceCount === 0) ? localize("가족 연결은 유지되고 있어요") : localize("{v0}명의 복용 상황을 확인 중", { v0: targets.filter(t => t.deviceCount !== 0).length })) : localize("가족이 보낸 코드를 입력해 연결해요")}
                    actionLabel={localize("추가")}
                    onAction={() => navigate('/family-care?role=guardian')}
                    sx={{ mt: 3 }}
                />
                {error ? (
                    <ConnectionRetryState onRetry={() => { setLoading(true); setError(false); fetchData(true); }} />
                ) : targets.length === 0 ? (
                    <Box sx={{ ...familyCardSx, textAlign: 'center', py: 5 }}>
                        <FamilyRestroomIcon sx={{ fontSize: 48, mb: 2, color: '#8a7000' }} />
                        <SingleLineText sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#1a1a1a', mb: 0.5 }}>{localize("챙기는 가족이 없어요")}</SingleLineText>
                        <Typography sx={{ color: '#777', fontSize: '1rem', mb: 3 }}>{localize("가족이 보낸 연결 코드를 입력하면")}{'\n'}{localize("약을 챙겼는지 확인할 수 있어요")}</Typography>
                        <Button variant="contained"
                            onClick={() => navigate('/family-care?role=guardian')}
                            sx={{ bgcolor: '#ffe566', '&:hover': { bgcolor: '#e8d040' }, color: '#5a4a00', borderRadius: 3, px: 4, py: 1.5, fontWeight: 700 }}>{localize("챙길 가족 추가")}</Button>
                    </Box>
                ) : (
                    targets.map((target) => {
                        const today = targetData[target.groupId];
                        const todayLoaded = today !== undefined;
                        const disconnected = target.deviceCount === 0;
                        const globallyPaused = isPauseActive(today?.globalPausedUntil);
                        const logs = disconnected ? [] : (today?.logs || []).filter(log => !globallyPaused || !['PENDING', 'MISSED'].includes(log.status));
                        const takenCount = logs.filter(l => l.status === 'TAKEN').length;
                        const totalCount = logs.length;
                        const pendingCount = logs.filter(l => l.status === 'PENDING').length;
                        const missedCount = logs.filter(l => l.status === 'MISSED').length;
                        const allDone = totalCount > 0 && pendingCount === 0 && missedCount === 0;
                        const actionableLogs = globallyPaused ? [] : logs.filter(l => l.status === 'PENDING' || l.status === 'MISSED');
                        const nudgeLogs = actionableLogs.filter(log => canNudgeGuardianLog(log));
                        const canSendNudge = nudgeLogs.length > 0;
                        const medicationNames = disconnected ? [] : (targetMedNames[target.groupId] || []);
                        const medicationSummary = formatMedicationSummary(medicationNames);

                        return (
                            <Box key={target.groupId} role="button" tabIndex={0} onClick={() => openSettings(target.linkId)}
                                sx={{ ...familyCardSx, cursor: 'pointer', '&:active': { bgcolor: '#fafafa' }, transition: 'background-color 0.15s' }}>
                                {/* 프로필 헤더 */}
                                <Box sx={{ ...familyCardHeaderSx, pb: totalCount > 0 ? 2 : 1 }}>
                                    <Box sx={{
                                        ...familyAvatarSx,
                                        bgcolor: disconnected ? '#f5f5f5' : allDone ? '#f0fdf4' : missedCount > 0 ? '#fef2f2' : '#fffbeb',
                                    }}>
                                        <FamilyRestroomIcon sx={{
                                            color: disconnected ? '#999' : allDone ? '#22c55e' : missedCount > 0 ? '#ef4444' : '#8a7000',
                                            fontSize: 24,
                                        }} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#1a1a1a', overflowWrap: 'anywhere' }}>
                                            {target.patientNickname || localize("가족 {v0}", { v0: targets.indexOf(target) + 1 })}
                                        </Typography>
                                        <Typography sx={{
                                            fontSize: '0.95rem', fontWeight: 600, mt: 0.2, wordBreak: 'keep-all',
                                            color: disconnected ? '#777' : allDone ? '#22c55e' : missedCount > 0 ? '#ef4444' : pendingCount > 0 ? '#8a7000' : '#777',
                                        }}>
                                            {disconnected ? localize("복용 현황 공유가 중단됐어요")
                                                : today?.loadError ? localize("현황을 불러오지 못했어요")
                                                : !todayLoaded ? localize("불러오는 중...")
                                                : globallyPaused ? localize("전체 복용 중지 중")
                                                : totalCount === 0 ? localize("오늘 복용 일정이 없어요")
                                                : allDone ? localize("오늘 약 모두 완료")
                                                : missedCount > 0 ? localize("미복용 {v0}건", { v0: missedCount })
                                                : localize("{v0}/{v1} 완료 · 대기 {v2}건", { v0: takenCount, v1: totalCount, v2: pendingCount })}
                                        </Typography>
                                    </Box>
                                    <IconButton aria-label={localize("{v0} 알림 설정", { v0: target.patientNickname || localize("가족 {v0}", { v0: targets.indexOf(target) + 1 }) })} onClick={(e) => { e.stopPropagation(); openSettings(target.linkId); }}
                                        sx={familyNameActionSx}>
                                        {settingsLoadingId === target.linkId ? <CircularProgress size={20} /> : <SettingsIcon sx={{ fontSize: 20 }} />}
                                    </IconButton>
                                </Box>

                                {disconnected && <Typography sx={{ px: 2, pb: 2, color: '#777', fontSize: '0.9rem', lineHeight: 1.6 }}>
                                    {localize(guardianSharingNotice(target))}
                                </Typography>}
                                {today?.loadError && <Button onClick={e => { e.stopPropagation(); fetchData(true); }}>{localize("다시 불러오기")}</Button>}
                                {/* 복용 진행률 바 */}
                                {totalCount > 0 && (
                                    <Box sx={{ px: 2, pb: 0.5 }}>
                                        <Box sx={{ height: 4, borderRadius: 2, bgcolor: '#f0f0f0', overflow: 'hidden' }}>
                                            <Box sx={{
                                                height: '100%', borderRadius: 2,
                                                bgcolor: allDone ? '#22c55e' : missedCount > 0 ? '#ef4444' : '#ffe566',
                                                width: `${(takenCount / totalCount) * 100}%`,
                                                transition: 'width 0.3s ease',
                                            }} />
                                        </Box>
                                    </Box>
                                )}

                                {/* 약 목록 - 오늘 로그가 있으면 복용 현황, 없으면 등록된 약 이름 */}
                                {totalCount > 0 ? (
                                    <Box sx={{ px: 2, py: 1 }}>
                                        {logs.map((log) => {
                                            const overdueMin = guardianOverdueMinutes(log);
                                            const isOverdue = overdueMin > 0;
                                            const isVeryOverdue = overdueMin >= 30;
                                            const pendingBg = isVeryOverdue ? '#fee2e2' : isOverdue ? '#ffedd5' : '#fffbeb';
                                            const pendingFg = isVeryOverdue ? '#ef4444' : isOverdue ? '#ea580c' : '#d97706';
                                            const overdueText = overdueMin >= 60
                                                ? localize("{v0}시간 지남", { v0: Math.floor(overdueMin / 60) })
                                                : localize("{v0}분 지남", { v0: overdueMin });
                                            const statusBg = log.status === 'TAKEN' ? '#f0fdf4'
                                                : log.status === 'MISSED' ? '#fef2f2'
                                                : log.status === 'SKIPPED' ? '#f5f5f5'
                                                : pendingBg;
                                            const statusFg = log.status === 'PENDING' ? pendingFg : getStatusColor(log.status);
                                            const statusText = log.status === 'PENDING' && isOverdue
                                                ? localize("대기 중 · {v0}", { v0: overdueText })
                                                : getStatusLabel(log.status);
                                            return (
                                            <Box key={log.id} sx={{
                                                display: 'grid',
                                                gridTemplateColumns: international ? '28px minmax(0, 1fr) auto' : '28px minmax(0, 1fr) 65px 132px',
                                                alignItems: 'center',
                                                columnGap: 1.2,
                                                py: 1,
                                                borderBottom: '1px solid #f8f8f8',
                                                '&:last-child': { borderBottom: 'none' },
                                            }}>
                                                <Box sx={{
                                                    width: 28, height: 28, borderRadius: '50%',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    bgcolor: log.status === 'TAKEN' ? '#f0fdf4'
                                                        : log.status === 'MISSED' ? '#fef2f2'
                                                        : log.status === 'SKIPPED' ? '#f5f5f5' : '#fffbeb',
                                                }}>
                                                    {log.status === 'TAKEN' && <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 16 }} />}
                                                    {log.status === 'MISSED' && <BlockIcon sx={{ color: '#ef4444', fontSize: 16 }} />}
                                                    {log.status === 'SKIPPED' && <SkipNextIcon sx={{ color: '#9ca3af', fontSize: 16 }} />}
                                                    {log.status === 'PENDING' && <AccessTimeIcon sx={{ color: pendingFg, fontSize: 16 }} />}
                                                </Box>
                                                <Typography sx={{
                                                    minWidth: 0,
                                                    fontSize: '1rem',
                                                    fontWeight: 600,
                                                    color: '#1a1a1a',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {log.medicationName}
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: '0.95rem',
                                                    color: '#999',
                                                    textAlign: 'right',
                                                    fontVariantNumeric: 'tabular-nums',
                                                }}>
                                                    {formatGuardianLogTime(log)}
                                                </Typography>
                                                <Box sx={{
                                                    px: 0.8, py: 0.2, borderRadius: 1.5, fontSize: '0.85rem', fontWeight: 600,
                                                    bgcolor: statusBg,
                                                    color: statusFg,
                                                    justifySelf: 'end',
                                                    width: '100%',
                                                    textAlign: 'center',
                                                    whiteSpace: 'nowrap',
                                                    ...(international && { gridColumn: '2 / -1', justifySelf: 'start', width: 'fit-content', maxWidth: '100%', boxSizing: 'border-box', whiteSpace: 'normal', overflowWrap: 'anywhere' }),
                                                }}>
                                                    {statusText}
                                                </Box>
                                            </Box>
                                            );
                                        })}
                                    </Box>
                                ) : todayLoaded && !globallyPaused && medicationNames.length > 0 && (
                                    <Box sx={{ px: 2, pt: 0.5, pb: 1.7 }}>
                                        <Box sx={{
                                            px: 1.4,
                                            py: 1,
                                            borderRadius: 2.5,
                                            bgcolor: '#fafafa',
                                            border: '1px solid #f0f0f0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}>
                                            <SingleLineText sx={{ flexShrink: 0, fontSize: '0.85rem', fontWeight: 800, color: '#8a7000' }}>{localize("등록된 약")}</SingleLineText>
                                            <Typography sx={{
                                                minWidth: 0,
                                                fontSize: '0.92rem',
                                                fontWeight: 600,
                                                color: '#555',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {medicationSummary}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                {/* 카드 footer — 챙겨달라고 알리기는 아직 처리할 약이 있을 때만 노출한다. */}
                                {(canSendNudge || actionableLogs.length > 0) && (
                                    <Box sx={{ borderTop: '1px solid #f0f0f0', bgcolor: '#fff', p: 1.5 }}>
                                        <Box sx={{
                                            display: 'grid',
                                            gridTemplateColumns: actionableLogs.length > 0
                                                ? 'minmax(0, 1fr) minmax(0, 1fr)'
                                                : 'minmax(0, 1fr)',
                                            gap: 1,
                                        }}>
                                            {canSendNudge && <Box
                                                component="button"
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setNudgeLimitReached(false);
                                                    setNudgeSheet({ target, logs: nudgeLogs });
                                                }}
                                                sx={{
                                                    border: '1px solid #f4cf63',
                                                    cursor: 'pointer',
                                                    bgcolor: '#fffdf2',
                                                    color: '#7a5c00',
                                                    borderRadius: 3,
                                                    py: 1.5,
                                                    px: 1.2,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    textAlign: 'center',
                                                    transition: 'background-color 0.15s ease, transform 0.15s ease',
                                                    '&:hover': { bgcolor: '#fff9db', borderColor: '#e8d040' },
                                                    '&:active': { bgcolor: '#fff3c4', transform: 'scale(0.98)' },
                                                }}
                                            >
                                                <SingleLineText component="div" variant="inherit" sx={{ fontSize: '0.94rem', fontWeight: 600, lineHeight: 1.25 }}>{localize("챙겨달라고 알리기")}</SingleLineText>
                                            </Box>}
                                            {actionableLogs.length > 0 && <Box
                                                component="button"
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setConfirmSheet({ target, logs: actionableLogs });
                                                }}
                                                sx={{
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    bgcolor: '#ffe566',
                                                    color: '#5a4a00',
                                                    borderRadius: 3,
                                                    py: 1.8,
                                                    px: 1.2,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    textAlign: 'center',
                                                    boxShadow: '0 2px 8px rgba(230,168,0,0.3)',
                                                    transition: 'background-color 0.15s ease, transform 0.15s ease',
                                                    '&:hover': { bgcolor: '#e8d040' },
                                                    '&:active': { bgcolor: '#e8d040', transform: 'scale(0.98)' },
                                                }}
                                            >
                                                <SingleLineText component="div" variant="inherit" sx={{ fontSize: '0.96rem', fontWeight: 700, lineHeight: 1.25 }}>{localize("대신 복용 처리하기")}</SingleLineText>
                                            </Box>}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        );
                    })
                )}
                {!error && targets.length > 0 && (
                    <Button fullWidth variant="outlined" startIcon={<PersonAddIcon />}
                        onClick={() => navigate('/family-care?role=guardian')}
                        sx={{
                            mt: 1, borderColor: '#e5e7eb', color: '#8a7000',
                            borderRadius: 3, py: 1.5, fontWeight: 700, fontSize: '1.05rem',
                            '&:hover': { borderColor: '#8a7000' },
                        }}>{localize("챙길 가족 추가")}</Button>
                )}

                <FamilySectionHeader
                    title={localize("나를 챙겨주는 가족")}
                    subtitle={myGuardians.length > 0 ? localize("{v0}명이 약 복용 상황을 함께 확인 중", { v0: myGuardians.length }) : localize("보호자에게 보낼 코드를 만들어요")}
                    actionLabel={localize("추가")}
                    onAction={() => navigate('/family-care?role=ward')}
                />
                {myGuardians.length === 0 ? (
                    <Box sx={{ ...familyCardSx, textAlign: 'center', py: 5 }}>
                        <FamilyRestroomIcon sx={{ fontSize: 48, mb: 2, color: '#8a7000' }} />
                        <SingleLineText sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#1a1a1a', mb: 0.5 }}>{localize("나를 챙겨주는 가족이 없어요")}</SingleLineText>
                        <Typography sx={{ color: '#777', fontSize: '1rem', mb: 3 }}>{localize("보호자에게 보낼 코드를 만들면")}{'\n'}{localize("여기에 표시돼요")}</Typography>
                        <Button variant="contained"
                            onClick={() => navigate('/family-care?role=ward')}
                            sx={{ bgcolor: '#ffe566', '&:hover': { bgcolor: '#e8d040' }, color: '#5a4a00', borderRadius: 3, px: 4, py: 1.5, fontWeight: 700 }}>{localize("보호자 추가")}</Button>
                    </Box>
                ) : (
                    <>
                        {myGuardians.map((guardian, idx) => {
                            const isEditing = editingNicknameId === guardian.linkId;
                            const displayName = guardian.guardianNickname || localize("보호자 {v0}", { v0: idx + 1 });
                            const deviceInfo = guardian.deviceModel || (guardian.platform === 'ios' ? 'iPhone' : guardian.platform === 'android' ? 'Android' : localize('기기'));
                            return (
                                <Box key={guardian.linkId}
                                    {...(isEditing ? {} : { role: 'button', tabIndex: 0, 'data-tappable': 'true' })}
                                    onClick={() => {
                                        if (!isEditing) {
                                            setEditingNicknameId(guardian.linkId);
                                            setEditingValue(guardian.guardianNickname || displayName);
                                        }
                                    }}
                                    sx={{
                                        ...familyCardSx,
                                        cursor: isEditing ? 'default' : 'pointer',
                                        '&:active': isEditing ? {} : { bgcolor: '#f9fafb' },
                                        transition: 'background-color 0.15s',
                                    }}>
                                    <Box sx={familyCardHeaderSx}>
                                        <Box sx={{
                                            ...familyAvatarSx,
                                            bgcolor: '#fffbeb',
                                        }}>
                                            <FamilyRestroomIcon sx={{ color: '#8a7000', fontSize: 24 }} />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            {isEditing ? (
                                                <TextField
                                                    size="small" placeholder={localize("별칭 입력 (예: 엄마)")}
                                                    value={editingValue}
                                                    onChange={(e) => setEditingValue(e.target.value)}
                                                    onKeyDown={async (e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            await handleSaveGuardianNickname(guardian);
                                                        }
                                                    }}
                                                    fullWidth autoFocus
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                                                    inputProps={{ style: { fontSize: '1.05rem', fontWeight: 600 } }}
                                                />
                                            ) : (
                                                <Box>
                                                    <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#1a1a1a', overflowWrap: 'anywhere' }}>
                                                        {displayName}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.95rem', color: '#999', mt: 0.2 }}>
                                                        {guardian.deviceCount === 0 ? localize("보호자의 현황 공유가 중단됐어요") : localize("{v0} · 함께 확인 중", { v0: deviceInfo })}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                        {isEditing ? (
                                            <Button
                                                variant="contained"
                                                onClick={(e) => { e.stopPropagation(); handleSaveGuardianNickname(guardian); }}
                                                disabled={savingNickname}
                                                onMouseDown={(e) => e.preventDefault()}
                                                sx={{
                                                    minWidth: 62,
                                                    height: 40,
                                                    px: 1.6,
                                                    borderRadius: 999,
                                                    bgcolor: '#ffe566',
                                                    color: '#5a4a00',
                                                    boxShadow: 'none',
                                                    fontSize: '0.9rem',
                                                    fontWeight: 800,
                                                    flexShrink: 0,
                                                    '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' },
                                                    '&:active': { bgcolor: '#e8d040', transform: 'scale(0.98)' },
                                                    '&.Mui-disabled': { bgcolor: '#f1f5f9', color: '#94a3b8' },
                                                }}>
                                                {savingNickname ? <CircularProgress size={18} sx={{ color: '#94a3b8' }} /> : localize("저장")}
                                            </Button>
                                        ) : (
                                            <IconButton onClick={(e) => {
                                                e.stopPropagation();
                                                setEditingNicknameId(guardian.linkId);
                                                setEditingValue(guardian.guardianNickname || displayName);
                                            }}
                                                sx={familyNameActionSx}>
                                                <EditIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        )}
                                    </Box>

                                    <Box sx={{ borderTop: '1px solid #f0f0f0', bgcolor: '#fff', p: 1.5 }}>
                                        <Box
                                            component="button"
                                            type="button"
                                            data-tappable="true"
                                            onClick={(e) => { e.stopPropagation(); setConfirmRemove(guardian); setConfirmRemoveOpen(true); }}
                                            sx={{
                                                width: '100%',
                                                border: '1px solid #fee2e2',
                                                cursor: 'pointer',
                                                bgcolor: '#fef2f2',
                                                color: '#ef4444',
                                                borderRadius: 3,
                                                py: 1.5,
                                                px: 1.2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                textAlign: 'center',
                                                transition: 'background-color 0.15s ease, transform 0.15s ease',
                                                '&:hover': { bgcolor: '#fee2e2' },
                                                '&:active': { bgcolor: '#fee2e2', transform: 'scale(0.98)' },
                                            }}
                                        >
                                            <SingleLineText component="div" variant="inherit" sx={{ fontSize: '0.94rem', fontWeight: 700, lineHeight: 1.25 }}>{localize("연결 해제")}</SingleLineText>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                        <Button fullWidth variant="outlined" startIcon={<PersonAddIcon />}
                            onClick={() => navigate('/family-care?role=ward')}
                            sx={{
                                mt: 1, borderColor: '#e5e7eb', color: '#8a7000',
                                borderRadius: 3, py: 1.5, fontWeight: 700, fontSize: '1.05rem',
                                bgcolor: '#fff',
                                '&:hover': { borderColor: '#8a7000', bgcolor: '#fffbeb' },
                            }}>{localize("보호자 추가")}</Button>
                    </>
                )}
            </Box>

            <AppToast
                open={snackbar.open}
                autoHideDuration={snackbar.severity === 'error' ? 4000 : 3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                severity={snackbar.severity}
                message={localize(snackbar.message)}
            />

            {/* 이름 편집과 알림 설정은 한 시트 안에서 분리한다. */}
            <BottomSheet open={!!settingsSheet}
                dismissDisabled={savingSettings || savingSettingsNickname}
                onBack={editingTargetNickname ? cancelTargetNickname : undefined}
                onClose={() => { beginRequest('settingsNickname'); setSettingsSheet(null); fetchData(true); }}
                footer={editingTargetNickname ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <AppButton tone="secondary" onClick={cancelTargetNickname} disabled={savingSettingsNickname} sx={{ flex: 1 }}>{localize("취소")}</AppButton>
                        <AppButton onClick={handleSaveTargetNickname}
                            disabled={savingSettingsNickname || savingSettings || nicknameTooLong || nicknameUnchanged}
                            sx={{ flex: 2 }}>{savingSettingsNickname ? <LoadingLabel>{localize("저장 중")}</LoadingLabel> : localize("저장")}</AppButton>
                    </Box>
                ) : undefined}>
                {editingTargetNickname ? (
                    <Box role="region" aria-labelledby="family-name-heading">
                        <SingleLineText id="family-name-heading" component="h2" sx={{ fontSize: '1.25rem', fontWeight: 800, color: '#1a1a1a', m: 0 }}>{localize("가족 이름 변경")}</SingleLineText>
                        <Typography sx={{ mt: 0.75, mb: 3, color: '#666', fontSize: '0.95rem', lineHeight: 1.6, wordBreak: 'keep-all' }}>{localize("가족 목록과 캘린더에 표시할 이름이에요.")}</Typography>
                        <Typography component="label" htmlFor="family-name-input" sx={{ display: 'block', mb: 1, fontSize: '0.95rem', fontWeight: 600, color: '#444' }}>{localize("이름")}</Typography>
                        <TextField id="family-name-input" fullWidth placeholder={localize("예: 엄마, 아빠")}
                            value={settingsNicknameDraft} disabled={savingSettingsNickname || savingSettings}
                            error={nicknameTooLong || !!nicknameError}
                            onChange={e => { setSettingsNicknameDraft(e.target.value); setNicknameError(''); }}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); handleSaveTargetNickname(); }
                            }}
                            inputProps={{ 'aria-describedby': 'family-name-help', 'data-keyboard-lift-action': 'true', enterKeyHint: 'done', autoComplete: 'off' }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#f9fafb', fontSize: '1.15rem', fontWeight: 600 },
                                '& .MuiOutlinedInput-input': { px: 1.75, py: 1.75 },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } }} />
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1 }}>
                            <Typography id="family-name-help" role={nicknameTooLong || nicknameError ? 'alert' : undefined}
                                sx={{ flex: 1, fontSize: '0.85rem', color: nicknameTooLong || nicknameError ? '#d32f2f' : '#777', lineHeight: 1.5, wordBreak: 'keep-all' }}>
                                {nicknameTooLong ? localize("이름은 30자까지 입력할 수 있어요.") : localize(nicknameError) || localize("비워 두면 ‘{v0}’로 표시돼요.", { v0: targetDefaultName })}
                            </Typography>
                            <Typography sx={{ fontSize: '0.85rem', color: nicknameTooLong ? '#d32f2f' : '#888', flexShrink: 0 }}>
                                {settingsNicknameDraft.trim().length}/30
                            </Typography>
                        </Box>
                    </Box>
                ) : <>
                {savingSettings && <LoadingLabel>{localize("설정 저장 중")}</LoadingLabel>}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <Box sx={{ ...familyAvatarSx, bgcolor: '#fffbeb' }}>
                        <FamilyRestroomIcon sx={{ color: '#8a7000', fontSize: 24 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: '#1a1a1a', overflowWrap: 'anywhere' }}>{targetDisplayName}</Typography>
                        <Typography sx={{ mt: 0.25, fontSize: '0.9rem', color: '#777' }}>{localize("가족 알림 설정")}</Typography>
                    </Box>
                    <Button disabled={savingSettings || savingSettingsNickname}
                        onClick={() => { setSettingsNicknameDraft(settingsForm.patientNickname); setNicknameError(''); setEditingTargetNickname(true); }}
                        sx={{ minHeight: 44, px: 1, color: '#666', fontSize: '0.9rem', fontWeight: 600, flexShrink: 0 }}>{localize("이름 변경")}</Button>
                </Box>

                {targetMeds.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, mb: 2.5 }}>
                        <SingleLineText sx={{ fontSize: '1.05rem', fontWeight: 600, color: '#888', mb: 1 }}>{localize("가족이 아직 약을 등록하지 않았어요.")}</SingleLineText>
                        <Typography sx={{ fontSize: '0.92rem', color: '#bbb', lineHeight: 1.6 }}>{localize("가족이 약을 등록하면")}{'\n'}{localize("알림 설정을 할 수 있어요.")}</Typography>
                    </Box>
                ) : (() => {
                    const activeMeds = targetMeds.filter(med => !settingsForm.excludedMedicationIds.includes(med.id));
                    const settingsActionDisabled = savingSettings || savingSettingsNickname;
                    const addTime = (timeStr, minutes) => {
                        const [h, m] = timeStr.split(':').map(Number);
                        const total = h * 60 + m + minutes;
                        return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
                    };
                    const Switch = ({ on, onClick }) => (
                        <Box onClick={settingsActionDisabled ? undefined : onClick} role="button"
                            sx={{ width: 48, height: 28, borderRadius: 14, cursor: settingsActionDisabled ? 'default' : 'pointer', ml: 1.5,
                                bgcolor: on ? '#ffe566' : '#d1d5db',
                                opacity: settingsActionDisabled ? 0.65 : 1,
                                position: 'relative', transition: 'all 0.25s ease', flexShrink: 0 }}>
                            <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'white',
                                position: 'absolute', top: 2, left: on ? 22 : 2,
                                transition: 'all 0.25s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                        </Box>
                    );
                    return (
                    <>
                        {/* 알림 받을 약 */}
                        <Box sx={{ mb: 3 }}>
                            <SingleLineText sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#666', mb: 1.2, px: 0.5 }}>{localize("알림 받을 약")}</SingleLineText>
                            {targetMeds.map((med) => {
                                const excluded = settingsForm.excludedMedicationIds.includes(med.id);
                                return (
                                    <Box key={med.id} sx={{
                                        display: 'flex', alignItems: 'center', p: 1.5, mb: 0.8,
                                        borderRadius: 3, bgcolor: excluded ? '#fafafa' : '#f9fafb',
                                        border: excluded ? '1px solid #f0f0f0' : '1px solid #e5e7eb',
                                        opacity: excluded ? 0.45 : 1,
                                        transition: 'all 0.2s ease',
                                    }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: '1.08rem', fontWeight: 700, color: '#1a1a1a' }}>
                                                {med.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.95rem', color: '#888', mt: 0.3 }}>{localize("매일 ")}{med.times.join(', ')}{localize(" 복용")}</Typography>
                                        </Box>
                                        <Switch on={!excluded} onClick={() => {
                                            saveSettingsChange({
                                                ...settingsForm,
                                                excludedMedicationIds: excluded
                                                    ? settingsForm.excludedMedicationIds.filter(id => id !== med.id)
                                                    : [...settingsForm.excludedMedicationIds, med.id],
                                            });
                                        }} />
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* 알림 설정 */}
                        <Box sx={{ mb: 3 }}>
                            <SingleLineText sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#666', mb: 1.2, px: 0.5 }}>{localize("언제 알려드릴까요?")}</SingleLineText>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {/* 아직 안 챙겼을 때 */}
                                <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#f9fafb' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <SingleLineText sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1a1a' }}>{localize("아직 안 챙겼을 때")}</SingleLineText>
                                        <Switch on={settingsForm.notifyDelayMinutes > 0} onClick={() => {
                                            const nextMinutes = settingsForm.notifyDelayMinutes > 0 ? 0 : 30;
                                            if (nextMinutes === 0) setCustomDelayInput(false);
                                            saveSettingsChange({
                                                ...settingsForm,
                                                notifyOnMissed: nextMinutes > 0 ? true : settingsForm.notifyOnMissed,
                                                notifyDelayMinutes: nextMinutes,
                                            });
                                        }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '0.95rem', color: '#777', mt: 0.5, lineHeight: 1.6 }}>{localize("챙길 시간이 지났는데 아직 완료하지 않았으면 알려드려요.")}</Typography>
                                    {settingsForm.notifyDelayMinutes > 0 && (() => {
                                        const minMissed = activeMeds.filter(m => m.reminderEnabled).reduce((min, m) => Math.min(min, m.maxReminderMinutes), Infinity);
                                        const maxAllowed = minMissed === Infinity ? 180 : minMissed - 1;
                                        const isOverLimit = maxAllowed > 0 && settingsForm.notifyDelayMinutes > maxAllowed;
                                        return (
                                        <>
                                            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fff', borderRadius: 2.5, border: '1px solid #f0f0f0' }}>
                                                <SingleLineText sx={{ fontSize: '0.92rem', color: '#888', mb: 1 }}>{localize("챙길 시간에서 몇 분 후에 알릴까요?")}</SingleLineText>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    {[10, 30, 60].map(m => {
                                                        const tooLong = maxAllowed > 0 && m > maxAllowed;
                                                        return (
                                                        <Box key={m} role="button" tabIndex={0} onClick={() => {
                                                            if (settingsActionDisabled || tooLong) return;
                                                            setCustomDelayInput(false);
                                                            saveSettingsChange({ ...settingsForm, notifyDelayMinutes: m });
                                                        }}
                                                            sx={{
                                                                flex: 1, py: 1, borderRadius: 2.5, textAlign: 'center',
                                                                cursor: settingsActionDisabled || tooLong ? 'default' : 'pointer',
                                                                fontWeight: 700, fontSize: '1rem',
                                                                bgcolor: tooLong ? '#f5f5f5' : (!customDelayInput && settingsForm.notifyDelayMinutes === m ? '#ffe566' : '#f5f5f5'),
                                                                color: tooLong ? '#d1d5db' : (!customDelayInput && settingsForm.notifyDelayMinutes === m ? '#5a4a00' : '#888'),
                                                                opacity: settingsActionDisabled || tooLong ? 0.5 : 1,
                                                                transition: 'all 0.2s',
                                                            }}>{m}{localize("분")}</Box>
                                                        );
                                                    })}
                                                    <Box role="button" tabIndex={0} onClick={() => {
                                                        if (settingsActionDisabled) return;
                                                        setCustomDelayInput(true);
                                                        setCustomDelayValue('');
                                                    }}
                                                        sx={{
                                                            flex: 1, py: 1, borderRadius: 2.5, textAlign: 'center', cursor: settingsActionDisabled ? 'default' : 'pointer',
                                                            fontWeight: 700, fontSize: '1rem',
                                                            bgcolor: customDelayInput ? '#ffe566' : '#f5f5f5',
                                                            color: customDelayInput ? '#5a4a00' : '#888',
                                                            opacity: settingsActionDisabled ? 0.5 : 1,
                                                            transition: 'all 0.2s',
                                                        }}>{localize("직접")}</Box>
                                                </Box>
                                                {customDelayInput && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.2, gap: 1 }}>
                                                        <TextField
                                                            disabled={settingsActionDisabled}
                                                            size="small" type="number" placeholder=""
                                                            value={customDelayValue}
                                                            onChange={(e) => {
                                                                const raw = e.target.value;
                                                                if (raw === '') { setCustomDelayValue(''); return; }
                                                                const num = parseInt(raw);
                                                                if (isNaN(num)) return;
                                                                const v = Math.min(maxAllowed > 0 ? maxAllowed : 180, num);
                                                                setCustomDelayValue(String(v));
                                                                if (v >= 1) saveSettingsChange({ ...settingsForm, notifyDelayMinutes: v });
                                                            }}
                                                            onBlur={() => {
                                                                if (!customDelayValue || parseInt(customDelayValue) < 1) {
                                                                    setCustomDelayValue(String(settingsForm.notifyDelayMinutes));
                                                                }
                                                            }}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            inputProps={{ min: 1, max: maxAllowed > 0 ? maxAllowed : 180, style: { textAlign: 'center', fontSize: '1.1rem', fontWeight: 700 } }}
                                                            sx={{ width: 80, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                                                        />
                                                        <Typography sx={{ fontSize: '1rem', color: '#666', fontWeight: 600 }}>{localize("분 ")}{maxAllowed > 0 && <Box component="span" sx={{ fontSize: '0.85rem', color: '#aaa' }}>{localize("(최대 ")}{maxAllowed}{localize("분)")}</Box>}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {isOverLimit && (
                                                    <Typography sx={{ fontSize: '0.88rem', color: '#ef4444', mt: 1, fontWeight: 600 }}>{localize("안먹음 시간(")}{minMissed}{localize("분)보다 짧아야 해요.")}</Typography>
                                                )}
                                            </Box>
                                            {/* 약별 알림 시각 */}
                                            {activeMeds.length > 0 && (
                                                <Box sx={{ mt: 1.5 }}>
                                                    {activeMeds.map((med) => {
                                                        const exceeded = med.reminderEnabled && settingsForm.notifyDelayMinutes >= med.maxReminderMinutes;
                                                        return (
                                                        <Box key={med.id} sx={{ mb: 1.2 }}>
                                                            <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: exceeded ? '#ef4444' : '#555', mb: 0.3 }}>
                                                                {med.name}
                                                                {!med.reminderEnabled && <Box component="span" sx={{ fontSize: '0.85rem', color: '#aaa', fontWeight: 500, ml: 0.5 }}>{localize("재알림 없는 약")}</Box>}
                                                                {exceeded && <Box component="span" sx={{ fontSize: '0.85rem', color: '#ef4444', fontWeight: 500, ml: 0.5 }}>{localize("안먹음 시간 초과")}</Box>}
                                                            </Typography>
                                                            {med.reminderEnabled && !exceeded && med.times.map((t) => (
                                                                <Typography key={t} sx={{ fontSize: '0.88rem', color: '#f59e0b', fontWeight: 600, ml: 0.5 }}>
                                                                    {t} → {addTime(t, settingsForm.notifyDelayMinutes)}{localize(" 알림")}</Typography>
                                                            ))}
                                                        </Box>
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        </>
                                        );
                                    })()}
                                </Box>

                                {/* 결국 안 챙겼을 때 */}
                                <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#f9fafb' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <SingleLineText sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1a1a' }}>{localize("결국 안 챙겼을 때")}</SingleLineText>
                                        <Switch on={settingsForm.notifyOnMissed} onClick={() => saveSettingsChange({ ...settingsForm, notifyOnMissed: !settingsForm.notifyOnMissed })} />
                                    </Box>
                                    <Typography sx={{ fontSize: '0.95rem', color: '#777', mt: 0.5, lineHeight: 1.6 }}>{localize("끝내 약을 챙기지 않으면 알려드려요. 아래 시각은 예상이며, 실제 첫 알림이나 미룬 시각에 따라 늦어질 수 있어요.")}</Typography>
                                    {settingsForm.notifyOnMissed && activeMeds.length > 0 && (
                                        <Box sx={{ mt: 1.5 }}>
                                            {activeMeds.map((med) => (
                                                <Box key={med.id} sx={{ mb: 1.2 }}>
                                                    <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#555', mb: 0.3 }}>
                                                        {med.name}
                                                    </Typography>
                                                    {med.times.map((t) => (
                                                        <Typography key={t} sx={{ fontSize: '0.88rem', color: '#ef4444', fontWeight: 600, ml: 0.5 }}>
                                                            {localize("{v0} → {v1} 이후 알림", { v0: t, v1: addTime(t, med.maxReminderMinutes > 0 ? med.maxReminderMinutes : 120) })}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>

                                {/* 약을 챙겼을 때 */}
                                <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#f9fafb' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <SingleLineText sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1a1a' }}>{localize("약을 챙겼을 때")}</SingleLineText>
                                        <Switch on={settingsForm.notifyOnTaken} onClick={() => saveSettingsChange({ ...settingsForm, notifyOnTaken: !settingsForm.notifyOnTaken })} />
                                    </Box>
                                    <Typography sx={{ fontSize: '0.95rem', color: '#777', mt: 0.5, lineHeight: 1.6 }}>{localize("가족의 복용·건너뛰기·완료 취소·나중에 먹기 기록을 알려드려요.")}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </>
                    );
                })()}

                </>}
            </BottomSheet>

            {/* 보호자 완료 처리 시트 */}
            <GuardianConfirmSheet
                open={!!confirmSheet}
                target={confirmSheet?.target}
                logs={confirmSheet?.logs || []}
                onClose={() => setConfirmSheet(null)}
                onApply={(ids, confirmedAt) => handleGuardianConfirm(confirmSheet.target.groupId, ids, confirmedAt)}
            />
            <GuardianNudgeSheet
                open={!!nudgeSheet}
                target={nudgeSheet?.target}
                logs={nudgeSheet?.logs || []}
                onClose={() => {
                    if (nudgeSubmitting || rewardSubmitting) return;
                    setNudgeSheet(null);
                    setNudgeLimitReached(false);
                    setNudgeLimitDialogOpen(false);
                }}
                onSubmit={handleGuardianNudge}
                onRequestReward={requestRewardedAd}
                submitting={nudgeSubmitting}
                rewardSubmitting={rewardSubmitting}
                limitReached={nudgeLimitReached}
                rewardAvailable={nudgeRewardAvailable}
            />
            <Dialog
                open={nudgeLimitDialogOpen}
                onClose={() => !rewardSubmitting && setNudgeLimitDialogOpen(false)}
                PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 340, mx: 3 } }}
            >
                <DialogContent sx={{ p: 3 }}>
                    <SingleLineText sx={{ fontSize: '1.2rem', fontWeight: 800, color: '#1a1a1a', mb: 1 }}>{localize("오늘 알림을 모두 사용했어요")}</SingleLineText>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: '#666', lineHeight: 1.55, mb: 2.5, wordBreak: 'keep-all', minWidth: 0, overflowWrap: 'anywhere' }}>
                        {nudgeRewardAvailable ? localize("광고 보고 3회 더 받을 수 있어요.") : localize("오늘 챙김 요청 30회를 모두 사용했어요. 내일 다시 이용해주세요.")}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: international ? 'column' : 'row' }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => setNudgeLimitDialogOpen(false)}
                            disabled={rewardSubmitting}
                            sx={{ borderRadius: 2, borderColor: '#e5e7eb', color: '#777', fontWeight: 700, py: 1.2 }}
                        >{localize("나중에")}</Button>
                        {nudgeRewardAvailable && <Button
                            fullWidth
                            variant="contained"
                            startIcon={<PlayCircleIcon />}
                            onClick={requestRewardedAd}
                            disabled={rewardSubmitting}
                            sx={{
                                bgcolor: '#ffe566',
                                color: '#5a4a00',
                                fontWeight: 800,
                                borderRadius: 2,
                                py: 1.2,
                                '&:hover': { bgcolor: '#e8d040' },
                            }}
                        >
                            {rewardSubmitting ? <LoadingLabel /> : localize('광고 보기')}
                        </Button>}
                    </Box>
                </DialogContent>
            </Dialog>
            <Dialog open={confirmRemoveOpen}
                onClose={() => { if (!removingGuardian) setConfirmRemoveOpen(false); }}
                TransitionProps={{ onExited: () => setConfirmRemove(null) }}
                PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 340, mx: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', pb: 0.5 }}>{localize("보호자 연결을 해제할까요?")}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#777', fontSize: '0.95rem' }}>
                        {confirmRemove?.guardianNickname || localize("보호자")}{localize("님과의 연결이 해제돼요.")}{'\n'}{localize("다시 연결하려면 코드를 새로 받아야 해요.")}</DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2 }}>
                    <Button disabled={removingGuardian} onClick={() => setConfirmRemoveOpen(false)}
                        sx={{ color: '#999', fontWeight: 600 }}>{localize("취소")}</Button>
                    <Button disabled={removingGuardian} onClick={handleRemoveMyGuardian}
                        sx={{ color: '#ef4444', fontWeight: 700 }}>{removingGuardian ? <LoadingLabel /> : localize("연결 해제")}</Button>
                </DialogActions>
            </Dialog>
            <ScrollDownIndicator />
        </Box>
    );
}
